import { LightningElement, api, wire } from "lwc";
import { assignedElements } from "common/slot";
import { BooleanAttr, attrToBool } from "common/helpers";
import * as locales from "./locales";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { LastPageCta } from "./types";

const allowedControlNames = ["a", "button", "tds-button", "tds-button-link"];

export default class extends LightningElement {
  @api open: BooleanAttr = null;
  @api assistiveText: string | null = null;
  @api agentforce: BooleanAttr | null = null;
  @api disableClose: BooleanAttr = null;
  @api lastPageCta: LastPageCta | null = null;

  private currentPage: number = 0;
  private pages: Element[] = [];
  private isModalCloseDisabled: boolean = true;
  private showDefaultControls: boolean = true;

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};

  @api
  nextPage() {
    if (this.currentPage < this.pages.length - 1) {
      this.currentPage++;
      this.showCurrentPage();
      this.updateModalDisableClose();
      this.updateShowDefaultControls();

      this.dispatchEvent(
        new CustomEvent("pagedmodalpagechange", {
          detail: { pageIndex: this.currentPage },
        })
      );
    }
  }

  private get isOpen() {
    return attrToBool(this.open);
  }

  private get isAgentforce() {
    return attrToBool(this.agentforce);
  }

  renderedCallback() {
    if (!this.disableClose) {
      window.addEventListener("keydown", this.handleKeyDown);
    }

    requestAnimationFrame(() => {
      const slot = this.template.querySelector(
        "slot[name='pages']"
      ) as HTMLSlotElement;

      if (slot) {
        this.pages = assignedElements(slot);
        this.showCurrentPage();
        this.updateModalDisableClose();
        this.updateShowDefaultControls();
      }
    });
  }

  disconnectedCallback() {
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  private get pagedModalContainerClass() {
    return this.totalPages > 1
      ? "th-paged-modal-container--with-pages"
      : "th-paged-modal-container";
  }

  private get hasLastPageCta() {
    return this.lastPageCta !== null;
  }

  private get rederLastPageCta() {
    return this.lastPage && this.hasLastPageCta;
  }

  private get hasLastPageCtaHeader() {
    return this.lastPageCta?.header;
  }

  private get hasLastPageCtaDesc() {
    return this.lastPageCta?.description;
  }

  private get lastPageCtaTarget() {
    if (this.lastPageCta?.target) {
      return this.lastPageCta.target;
    } else {
      return "_self";
    }
  }

  private handleRequestClose() {
    this.open = false;
  }

  private handleKeyDown = (e: KeyboardEventInit) => {
    if (this.lastPage && (e.key === "Esc" || e.key === "Escape")) {
      this.handleRequestClose();
    }
  };

  private updateShowDefaultControls() {
    const currentPageElement = this.pages[this.currentPage] as HTMLSlotElement;

    if (!currentPageElement) {
      this.showDefaultControls = true;
      return;
    }

    const controlsSlot = currentPageElement.querySelector(
      '[slot="controls"]'
    ) as HTMLSlotElement;

    if (!controlsSlot) {
      this.showDefaultControls = true;
      return;
    }

    const hasValidControl = allowedControlNames.includes(
      controlsSlot.tagName.toLowerCase()
    );

    this.showDefaultControls = !hasValidControl;
  }

  private showCurrentPage() {
    this.pages.forEach((el, i) => {
      const element = el as HTMLElement;
      if (i === this.currentPage) {
        element.style.display = "";
      } else {
        element.style.display = "none";
      }
    });
  }

  private updateModalDisableClose() {
    this.isModalCloseDisabled = this.currentPage < this.pages.length - 1;
  }

  private get totalPages() {
    return this.pages.length;
  }

  private get indicators() {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  private isCurrent(index: number): boolean {
    return index === this.currentPage;
  }

  private indexClass(index: number): string {
    return index === this.currentPage ? "active" : "";
  }

  private get lastPage() {
    return this.currentPage >= this.totalPages - 1;
  }

  private get renderNextPageIndicators() {
    return this.pages.length > 1;
  }

  private get indicatorsWithClass() {
    return this.indicators.map((_, i) => ({
      index: i,
      className: i === this.currentPage ? "active" : "",
    }));
  }
}
